import { captureError, trackEvent } from '@/lib/monitoring'

export interface ISBNBookData {
  isbn: string
  title: string
  authors: string[]
  description?: string
  imageUrl?: string
  publishedDate?: string
  publisher?: string
  pageCount?: number
  categories?: string[]
  language?: string
}

export interface GoogleBooksVolumeInfo {
  title: string
  authors?: string[]
  description?: string
  imageLinks?: {
    thumbnail?: string
    small?: string
    medium?: string
    large?: string
    smallThumbnail?: string
  }
  publishedDate?: string
  publisher?: string
  pageCount?: number
  categories?: string[]
  language?: string
}

export interface GoogleBooksVolume {
  id: string
  volumeInfo: GoogleBooksVolumeInfo
}

export interface GoogleBooksResponse {
  totalItems: number
  items?: GoogleBooksVolume[]
}

/**
 * Fetches book data from Google Books API using ISBN
 */
async function fetchFromGoogleBooks(isbn: string): Promise<ISBNBookData | null> {
  try {
    const cleanISBN = isbn.replace(/[- ]/g, '')

    // Try multiple search strategies
    const searches = [
      `isbn:${cleanISBN}`,
      cleanISBN,
      `${cleanISBN.slice(0, 3)}-${cleanISBN.slice(3, 4)}-${cleanISBN.slice(4, 7)}-${cleanISBN.slice(7, 12)}-${cleanISBN.slice(12)}`, // formatted ISBN-13
    ]

    let data: GoogleBooksResponse | null = null

    for (const searchQuery of searches) {
      const url = `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(searchQuery)}`

      const response = await fetch(url)

      if (!response.ok) {
        continue
      }

      const responseData: GoogleBooksResponse = await response.json()

      if (responseData.items && responseData.items.length > 0) {
        data = responseData
        break
      }
    }

    if (!data || !data.items || data.items.length === 0) {
      return null
    }

    const book = data.items[0]
    const volumeInfo = book.volumeInfo

    // Get the best available image URL
    const imageUrl =
      volumeInfo.imageLinks?.large ||
      volumeInfo.imageLinks?.medium ||
      volumeInfo.imageLinks?.small ||
      volumeInfo.imageLinks?.thumbnail ||
      volumeInfo.imageLinks?.smallThumbnail

    return {
      isbn: cleanISBN,
      title: volumeInfo.title || '',
      authors: volumeInfo.authors || [],
      description: volumeInfo.description,
      imageUrl,
      publishedDate: volumeInfo.publishedDate,
      publisher: volumeInfo.publisher,
      pageCount: volumeInfo.pageCount,
      categories: volumeInfo.categories,
      language: volumeInfo.language,
    }
  } catch (_error) {
    return null
  }
}

/**
 * Fetches book data from OpenLibrary API using ISBN
 */
async function fetchFromOpenLibrary(isbn: string): Promise<ISBNBookData | null> {
  try {
    const cleanISBN = isbn.replace(/[- ]/g, '')

    // OpenLibrary ISBN API
    const response = await fetch(
      `https://openlibrary.org/api/books?bibkeys=ISBN:${cleanISBN}&format=json&jscmd=data`
    )

    if (!response.ok) {
      return null
    }

    const data = await response.json()
    const bookKey = `ISBN:${cleanISBN}`
    const book = data[bookKey]

    if (!book) {
      return null
    }

    // Extract cover image
    let imageUrl: string | undefined
    if (book.cover?.large) {
      imageUrl = book.cover.large
    } else if (book.cover?.medium) {
      imageUrl = book.cover.medium
    } else if (book.cover?.small) {
      imageUrl = book.cover.small
    }

    // Extract categories - filter out technical/numeric subjects
    const categories = book.subjects
      ?.filter((subject: { name?: string } | string) => {
        const name = typeof subject === 'string' ? subject : subject.name || ''
        // Filter out dewey numbers, classification codes, etc
        return !/^[0-9.]+$/.test(name) && !name.toLowerCase().startsWith('qa') && name.length > 3
      })
      ?.map((subject: { name?: string } | string) =>
        typeof subject === 'string' ? subject : subject.name || ''
      )
      ?.slice(0, 5) // Limit to 5 most relevant categories

    return {
      isbn: cleanISBN,
      title: book.title || '',
      authors: book.authors?.map((author: { name: string }) => author.name) || [],
      description: book.excerpts?.[0]?.text || book.notes,
      imageUrl,
      publishedDate: book.publish_date,
      publisher: book.publishers?.[0]?.name,
      pageCount: book.number_of_pages,
      categories,
      language: book.languages?.[0]?.key?.replace('/languages/', '') || 'en',
    }
  } catch (_error) {
    return null
  }
}

/**
 * Fetches book data using multiple APIs with fallback strategy
 * Prioritizes APIs that have better coverage for Brazilian books
 */
export async function fetchBookDataFromAPI(isbn: string): Promise<ISBNBookData | null> {
  const startTime = performance.now()
  // Clean ISBN (remove hyphens and spaces)
  const cleanISBN = isbn.replace(/[- ]/g, '')

  // Validate ISBN format (10 or 13 digits)
  if (!/^\d{10}$|^\d{13}$/.test(cleanISBN)) {
    const error = new Error('errors.invalidISBN')
    captureError(error, { isbn: cleanISBN })
    trackEvent('isbn_lookup_failed', {
      error_type: 'invalid_format',
      isbn: cleanISBN,
    })
    throw error
  }

  // Try APIs in order of preference for Brazilian books
  // 1. OpenLibrary (excellent international coverage, good for BR books)
  // 2. Google Books (standard but limited for some regions)

  const apiProviders = [
    { name: 'OpenLibrary', fetch: fetchFromOpenLibrary },
    { name: 'Google Books', fetch: fetchFromGoogleBooks },
  ]

  for (const provider of apiProviders) {
    try {
      const result = await provider.fetch(cleanISBN)

      if (result?.title) {
        const responseTime = performance.now() - startTime
        trackEvent('isbn_lookup_success', {
          provider: provider.name,
          response_time_ms: Math.round(responseTime),
        })
        return result
      }
    } catch (_error) {}
  }

  // If we get here, no provider returned valid data
  trackEvent('isbn_lookup_failed', {
    error_type: 'no_data_found',
    isbn: cleanISBN,
  })

  return null
}

/**
 * Validates ISBN-10 or ISBN-13 format
 */
export function isValidISBN(isbn: string): boolean {
  const cleanISBN = isbn.replace(/[- ]/g, '')
  return /^\d{10}$|^\d{13}$/.test(cleanISBN)
}

/**
 * Formats ISBN with hyphens for display
 */
export function formatISBN(isbn: string): string {
  const cleanISBN = isbn.replace(/[- ]/g, '')

  if (cleanISBN.length === 10) {
    // ISBN-10 format: X-XXX-XXXXX-X
    return `${cleanISBN.slice(0, 1)}-${cleanISBN.slice(1, 4)}-${cleanISBN.slice(4, 9)}-${cleanISBN.slice(9)}`
  } else if (cleanISBN.length === 13) {
    // ISBN-13 format: XXX-X-XXX-XXXXX-X
    return `${cleanISBN.slice(0, 3)}-${cleanISBN.slice(3, 4)}-${cleanISBN.slice(4, 7)}-${cleanISBN.slice(7, 12)}-${cleanISBN.slice(12)}`
  }

  return isbn // Return as-is if invalid
}
